import React from 'react';
import type { SearchResultWithUIState, SearchState } from './types';
import type { SORTDIR } from 'venn-components';
import { ConditionalOverlay } from 'venn-components';
import EmptyStateV2 from './EmptyStateV2';
import LibrariesTable from './LibrariesTable';
import type { MetricFilter } from 'venn-api';
import { getTextThemeProvider } from 'venn-ui-kit';

interface LibraryResultsProps {
  data: SearchResultWithUIState[];
  sortDir: SORTDIR.ASC | SORTDIR.DESC;
  sortKey?: string;
  onSort: (key: string, dir: SORTDIR) => void;
  updateData: React.Dispatch<React.SetStateAction<SearchState>>;
  refetchSearch: (hideLoading?: boolean) => Promise<void>;
  /** Clears the search query (and the filters in v2) */
  onClear: () => void;
  /** Whether there is a search query or not */
  canClearQuery: boolean;
  /** Whether the current filters are different from the default or not */
  canClearFilters: boolean;
  loading?: boolean;
  disableSort?: boolean;
  metricFilters?: MetricFilter[];
  showAssetType?: boolean;
  showMorningstarCategory?: boolean;
  findSimilar: (item: SearchResultWithUIState) => void;
  trackSelectionMade: (item: SearchResultWithUIState) => void;
  onToggleSelection?: (item: SearchResultWithUIState) => void;
  onToggleAllSelection?: (checked: boolean, items: SearchResultWithUIState[]) => void;
}

const LibraryResults = ({
  data,
  onSort,
  sortDir,
  sortKey,
  updateData,
  refetchSearch,
  onClear,
  loading,
  disableSort,
  canClearQuery,
  canClearFilters,
  trackSelectionMade,
  showAssetType,
  showMorningstarCategory,
  findSimilar,
  metricFilters = [],
  onToggleSelection,
  onToggleAllSelection,
}: LibraryResultsProps) => {
  const showEmptyState = !data.length && !loading;
  return (
    <div>
      <ConditionalOverlay condition={!!loading} className="qa-library-table">
        <LibrariesTable
          onSort={onSort}
          sortDir={sortDir}
          sortKey={sortKey}
          updateData={updateData}
          refetchSearch={refetchSearch}
          disableSort={disableSort}
          data={data}
          trackSelectionMade={trackSelectionMade}
          findSimilar={findSimilar}
          showAssetType={showAssetType}
          showMorningstarCategory={showMorningstarCategory}
          metricFilters={metricFilters}
          onToggleSelection={onToggleSelection}
          onToggleAllSelection={onToggleAllSelection}
        />
        {!loading && (
          <div className="my-[8px] text-venn-grey-70 text-[1rem]">
            * These metrics are calculated using the overlapping date range of our{' '}
            {getTextThemeProvider().factorLensName}, investment allocation date ranges, and the available timeseries for
            the investment or portfolio.
          </div>
        )}
      </ConditionalOverlay>

      {showEmptyState ? (
        <EmptyStateV2 onClear={onClear} canClearQuery={canClearQuery} canClearFilters={canClearFilters} />
      ) : null}
    </div>
  );
};

export default LibraryResults;
